import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Loader2, CreditCard, CheckCircle2, AlertTriangle, ArrowLeft, Sparkles, CalendarClock } from "lucide-react";
import { toast } from "sonner";

const STATUS_UI = {
  trialing: { label: "Free trial", color: "bg-blue-100 text-blue-800 border-blue-200" },
  active: { label: "Active", color: "bg-emerald-100 text-emerald-800 border-emerald-200" },
  past_due: { label: "Payment failed", color: "bg-amber-100 text-amber-800 border-amber-200" },
  cancelled: { label: "Cancelled", color: "bg-red-100 text-red-800 border-red-200" },
  expired: { label: "Trial ended", color: "bg-red-100 text-red-800 border-red-200" },
};

const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" }) : "—";

export default function Billing() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/subscription/me");
      setSub(res.data);
    } catch (e) { /* */ } finally { setLoading(false); }
  }, []);

  // Poll payment status after returning from Stripe
  const pollStatus = useCallback(async (sessionId, attempt = 0) => {
    if (attempt > 8) { setVerifying(false); toast.info("Still processing — refresh in a moment."); load(); return; }
    try {
      const res = await api.get(`/payments/status/${sessionId}`);
      if (res.data.payment_status === "paid") {
        setVerifying(false);
        toast.success("Subscription active — your farm is live!");
        setParams({}, { replace: true });
        load();
        return;
      }
    } catch (e) { /* */ }
    setTimeout(() => pollStatus(sessionId, attempt + 1), 2000);
  }, [load, setParams]);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (sessionId) {
      setVerifying(true);
      pollStatus(sessionId);
    }
    load();
  }, []); // eslint-disable-line

  const subscribe = async () => {
    setBusy(true);
    try {
      const res = await api.post("/subscription/checkout", { origin_url: window.location.origin });
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout");
      setBusy(false);
    }
  };

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await api.post("/subscription/portal", { origin_url: window.location.origin });
      window.location.href = res.data.portal_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not open billing portal");
      setBusy(false);
    }
  };

  if (loading) return <div className="pt-32 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  if (!sub?.has_farm) {
    return (
      <div className="pt-16 min-h-screen grid place-items-center px-4">
        <Card className="rounded-2xl p-8 text-center max-w-md">
          <h2 className="text-xl font-semibold">Set up your farm first</h2>
          <p className="text-muted-foreground text-sm mt-1">Register your farm to start your free trial.</p>
          <Button className="mt-4" onClick={() => navigate("/sell/register")}>Register your farm</Button>
        </Card>
      </div>
    );
  }

  const status = sub.subscription_status;
  const ui = STATUS_UI[status] || STATUS_UI.trialing;
  const needsSubscribe = ["expired", "cancelled", "past_due", "trialing"].includes(status);
  const isActive = status === "active";

  return (
    <div className="pt-16 min-h-screen">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6">
        <Button variant="ghost" size="sm" className="mb-3" onClick={() => navigate("/sell/dashboard")}>
          <ArrowLeft className="h-4 w-4 mr-1" /> Back to dashboard
        </Button>
        <div className="flex items-center gap-2 mb-5">
          <CreditCard className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold">Billing & subscription</h1>
        </div>

        {verifying && (
          <Card className="rounded-2xl p-4 mb-4 flex items-center gap-3 bg-secondary/50" data-testid="billing-verifying">
            <Loader2 className="h-5 w-5 animate-spin" /> Confirming your payment…
          </Card>
        )}

        {/* Status card */}
        <Card className="rounded-2xl p-6" data-testid="billing-status-card">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="text-sm text-muted-foreground">Farm Gate Seller Plan</div>
              <div className="text-3xl font-semibold mt-1">{sub.price_display}<span className="text-base font-normal text-muted-foreground">/month</span></div>
            </div>
            <Badge className={ui.color} data-testid="billing-status-badge">{ui.label}</Badge>
          </div>

          <Separator className="my-4" />

          {status === "trialing" && (
            <div className="flex items-center gap-2 text-sm">
              <CalendarClock className="h-4 w-4 text-primary" />
              <span><span className="font-semibold" data-testid="billing-trial-days">{sub.trial_days_left} days</span> left in your free trial (ends {fmtDate(sub.trial_ends_at)}). Subscribe any time to continue after it ends.</span>
            </div>
          )}
          {isActive && (
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-emerald-700"><CheckCircle2 className="h-4 w-4" /> Your subscription is active and your farm is visible to shoppers.</div>
              {sub.next_billing_date && <div className="text-muted-foreground">Next billing date: <span className="font-medium text-foreground">{fmtDate(sub.next_billing_date)}</span></div>}
              {sub.cancel_at_period_end && <div className="text-amber-700">Your plan will not renew — access continues until {fmtDate(sub.next_billing_date)}.</div>}
            </div>
          )}
          {["expired", "cancelled"].includes(status) && (
            <div className="flex items-start gap-2 text-sm text-red-700" data-testid="billing-ended-message">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span>Your free trial has ended. Subscribe to make your farm visible again to shoppers. Your farm and products are safe — nothing has been deleted.</span>
            </div>
          )}
          {status === "past_due" && (
            <div className="flex items-start gap-2 text-sm text-amber-700">
              <AlertTriangle className="h-4 w-4 mt-0.5" />
              <span>Your last payment failed and your listing is hidden. Update your card to restore your subscription.</span>
            </div>
          )}

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            {needsSubscribe && (
              <Button className="flex-1 bg-[color:var(--fg-wood-600)] hover:bg-[color:var(--fg-wood-700)] text-white" onClick={subscribe} disabled={busy} data-testid="billing-subscribe-button">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Sparkles className="h-4 w-4 mr-1" />}
                {status === "trialing" ? "Subscribe now" : "Reactivate subscription"}
              </Button>
            )}
            {sub.has_customer && (
              <Button variant="secondary" className="flex-1" onClick={openPortal} disabled={busy} data-testid="billing-portal-button">
                {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <CreditCard className="h-4 w-4 mr-1" />}
                Manage billing (update card / cancel)
              </Button>
            )}
          </div>
        </Card>

        {/* What you get */}
        <Card className="rounded-2xl p-6 mt-4">
          <h3 className="font-semibold mb-3">What's included</h3>
          <ul className="space-y-2 text-sm">
            {["Your farm listed on the live map", "Unlimited product listings", "Receive and manage pre-orders", "Verified organic badge (with certification)"].map((t) => (
              <li key={t} className="flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-emerald-600" /> {t}</li>
            ))}
          </ul>
          <p className="text-xs text-muted-foreground mt-4">Billed monthly via Stripe. Cancel any time from the billing portal. Shoppers are always free.</p>
        </Card>
      </div>
    </div>
  );
}
